import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Users, FileText, Send, CheckCircle, Clock, XCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardEstadisticas() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/stats/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Error al cargar estadísticas');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast.error('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, color, subtext }) => (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
        </div>
        <div className={`p-4 rounded-full ${color}`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-lg text-slate-500">Cargando estadísticas...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">Dashboard - Estadísticas</h1>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            icon={Users} 
            title="Total Usuarios" 
            value={stats?.usuarios?.total || 0}
            color="bg-blue-500"
            subtext={`${stats?.usuarios?.natural || 0} naturales, ${stats?.usuarios?.juridica || 0} jurídicas`}
          />
          <StatCard 
            icon={FileText} 
            title="Convocatorias" 
            value={stats?.convocatorias?.total || 0}
            color="bg-purple-500"
            subtext={`${stats?.convocatorias?.activas || 0} activas`}
          />
          <StatCard 
            icon={Send} 
            title="Postulaciones" 
            value={stats?.postulaciones?.total || 0}
            color="bg-green-500"
            subtext={`${stats?.postulaciones?.pendientes || 0} pendientes`}
          />
          <StatCard 
            icon={TrendingUp} 
            title="Tasa Aprobación" 
            value={`${stats?.postulaciones?.tasa_aprobacion || 0}%`}
            color="bg-orange-500"
          />
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Users by Type */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Usuarios por Tipo</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-slate-700">Persona Natural</span>
                </div>
                <span className="font-semibold">{stats?.usuarios?.natural || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span className="text-slate-700">Persona Jurídica</span>
                </div>
                <span className="font-semibold">{stats?.usuarios?.juridica || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-slate-700">Funcionarios</span>
                </div>
                <span className="font-semibold">{stats?.usuarios?.funcionarios || 0}</span>
              </div>
              <div className="flex items-center justify-between border-t pt-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-slate-700">Usuarios Verificados</span>
                </div>
                <span className="font-semibold text-green-600">{stats?.usuarios?.verificados || 0}</span>
              </div>
            </div>
          </div>

          {/* Postulaciones by Status */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Estado de Postulaciones</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span className="text-slate-700">Pendientes</span>
                </div>
                <span className="font-semibold text-yellow-600">{stats?.postulaciones?.pendientes || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-slate-700">En Revisión</span>
                </div>
                <span className="font-semibold text-blue-600">{stats?.postulaciones?.en_revision || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-slate-700">Aprobadas</span>
                </div>
                <span className="font-semibold text-green-600">{stats?.postulaciones?.aprobadas || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="text-slate-700">Rechazadas</span>
                </div>
                <span className="font-semibold text-red-600">{stats?.postulaciones?.rechazadas || 0}</span>
              </div>
            </div>
          </div>

          {/* Convocatorias by Status */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Convocatorias por Estado</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-slate-700">Activas</span>
                </div>
                <span className="font-semibold text-green-600">{stats?.convocatorias?.activas || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-slate-700">Próximas</span>
                </div>
                <span className="font-semibold text-yellow-600">{stats?.convocatorias?.proximas || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-slate-700">Cerradas</span>
                </div>
                <span className="font-semibold text-red-600">{stats?.convocatorias?.cerradas || 0}</span>
              </div>
            </div>
          </div>

          {/* Top Convocatorias */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Convocatorias con más Postulaciones</h3>
            <div className="space-y-3">
              {stats?.top_convocatorias?.length > 0 ? (
                stats.top_convocatorias.map((conv, index) => (
                  <div key={conv._id || index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-slate-700 text-sm truncate max-w-[250px]">{conv.nombre}</span>
                    <span className="font-semibold text-blue-600">{conv.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-sm">No hay datos disponibles</p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
